import PaystackPop from "@paystack/inline-js";

/**
 * Paystack's inline checkout.
 *
 * The sheet opens over the app rather than sending the student to a hosted
 * page, which is what lets them pick card, bank transfer or USSD in one place
 * — on a Nigerian connection a full-page redirect away and back is where
 * payments get abandoned.
 *
 * The public key is public by design: it can only start a checkout. Every
 * number that matters is still the server's — the reference and the amount are
 * fixed before the sheet opens, and what the sheet reports is re-read from
 * Paystack before anything is unlocked.
 */
export function paystackPublicKey(): string | null {
  const key = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
  return typeof key === "string" && key.trim().length > 0 ? key.trim() : null;
}

export interface OpenCheckoutOptions {
  reference: string;
  email: string;
  amountMinor: number;
  onSuccess: (reference: string) => void;
  onCancel: () => void;
  /** The sheet is up and showing. */
  onLoad?: (() => void) | undefined;
}

/**
 * Open the sheet.
 *
 * Throws when the key is missing rather than opening a checkout that cannot
 * complete: a student watching a blank sheet has no way to know the app was
 * misconfigured, and "that payment didn't go through" would be a lie about
 * a payment that was never attempted.
 */
export function openPaystackCheckout(options: OpenCheckoutOptions): void {
  const key = paystackPublicKey();
  if (!key) {
    throw new Error("Payments aren't set up on this build — VITE_PAYSTACK_PUBLIC_KEY is missing.");
  }

  /*
   * No `currency`, deliberately.
   *
   * Paystack rejects a currency the merchant account is not enabled for —
   * "Currency not supported by merchant" — and sending one explicitly is the
   * only way to hit that. Omitted, the account's own currency is used, which
   * for a Nigerian account is Naira. v1 never sent it either, against this
   * same merchant.
   *
   * This is not the client choosing what to charge. The amount is still the
   * server's, and activation re-reads the charge from Paystack and refuses it
   * unless both the amount and the currency match what config says — so an
   * account that turned out to settle in something else would unlock nothing
   * rather than quietly sell access for the wrong money.
   */
  new PaystackPop().newTransaction({
    key,
    email: options.email,
    amount: options.amountMinor,
    // Ours, generated server-side, already written to a pending payment row.
    // Letting Paystack generate one would leave nothing to reconcile against.
    reference: options.reference,
    onSuccess: (transaction: { reference: string }) => options.onSuccess(transaction.reference),
    onCancel: options.onCancel,
    ...(options.onLoad ? { onLoad: () => options.onLoad?.() } : {}),
  });
}
